import { type ReactElement, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { ApiRoute, BreakpointMedia } from 'config';
import { get } from 'utils/network';
import { getQueryParam } from 'utils/location';

import useAsyncEffect from 'hooks/useAsyncEffect';
import useSubscription from 'hooks/useSubscription';

import Plan from 'models/Plan';
import IPlanInfo from 'models/IPlanInfo';

import Steps from './Steps';
import PreselectedPlanBanner from './PreselectedPlanBanner';
import CheckoutSummaryContainer from './CheckoutSummaryContainer';
import OrderSummary from '../../OrderSummary';
import SignupHeader from '../DiversifiedPlan/SignupHeader';

type Props = {
  isLoggedOut?: boolean;
  preselectedPlan?: Plan;
  menuShippingDate?: string;
};

function Checkout({
  isLoggedOut,
  preselectedPlan,
  menuShippingDate,
}: Props): ReactElement {
  const isDiversifiedPlansEnabled = window?.gon?.featureFlags?.isDiversifiedPlansEnabled || false;
  const { subscription } = useSubscription();

  const { signup_summary: signupSummary } = subscription;

  let hasGiveaway = false;
  let hasGift = false;
  let hasTotalValueCovered = false;
  if (signupSummary) {
    hasGiveaway = signupSummary?.discount?.type === 'giveaway_discount';
    hasGift = signupSummary?.discount?.type === 'gift_discount';
    hasTotalValueCovered = signupSummary?.box_total === 0;
  }

  const hasRecommendedPLan = !!getQueryParam('quiz_plan_recommendation');
  const showSummaryAsDrawer = !useMediaQuery({ query: BreakpointMedia.LG });

  const [isPlanInitialized, setPlanInitialized] = useState(Plan.isInitialized);

  useAsyncEffect(async (isMounted) => {
    if (!Plan.isInitialized) {
      const response = await get<IPlanInfo[]>(ApiRoute.PLANS_SHORT);
      // @ts-expect-error TODO: fix null safety
      Plan.initialize(response.parsedBody);
      if (isMounted) setPlanInitialized(true);
    }
  }, []);

  const showSummary = isLoggedOut || (!isLoggedOut && signupSummary);
  const plan = isLoggedOut ? preselectedPlan : Plan.getPlan();

  const summaryDrawer = (
    <CheckoutSummaryContainer
      loggedIn={!isLoggedOut}
      menuShippingDate={menuShippingDate}
      // @ts-expect-error TODO: fix null safety
      selectedPlan={plan}
    />
  );

  /*
  * Only rendering this component as a side panel, for larger breakpoints. We will use
  * SignupHeader.tsx as a separate drawer component for smaller breakpoints.
  * */
  const diversifiedSummaryPanel = (
    <>
      {!showSummaryAsDrawer && (
        <OrderSummary
          // @ts-expect-error TODO: fix null safety
          existingPlan={plan}
          menuShippingDate={menuShippingDate || signupSummary?.delivery_date}
          loggedIn={!isLoggedOut}
        />
      )}
    </>
  );

  function SideViewWrapper(isLoggedIn, orderSummary) {
    return isLoggedIn ? (
      <div className="c-accordion-container c-accordion-container--signup">
        {isDiversifiedPlansEnabled && showSummaryAsDrawer && (
          <SignupHeader
            preselectedPlan={
              new Plan(subscription?.plan, subscription?.extras, subscription?.is_diversified)
            }
            menuShippingDate={signupSummary?.delivery_date}
            // @ts-expect-error TODO: fix null safety
            isLoggedOut={isLoggedOut}
          />
        )}
        <div className="l-container">
          <div className="row justify-content-center">
            <div
              className={[
                'col-12',
                'col-md-10',
                'col-lg-8',
                'c-accordion-container__main-content',
              ].join(' ')}
              style={isDiversifiedPlansEnabled ? { paddingTop: '10px' } : {}}
            >
              <div className="c-signup-checkout">
                {(hasGiveaway || (hasGift && hasTotalValueCovered)) && (
                  <p className="u-text u-text--lg u-text--center">
                    <br />
                    Complete your account to receive your&nbsp;
                    <strong>Purple Carrot Gift.</strong>
                  </p>
                )}
                <Steps />
              </div>
            </div>
            <div className="col-xs-auto col-lg-4">{orderSummary}</div>
          </div>
        </div>
      </div>
    ) : (
      orderSummary
    );
  }

  return (
    <>
      {isPlanInitialized && (
        isDiversifiedPlansEnabled ? (
          <>
            {showSummary && SideViewWrapper(!isLoggedOut, diversifiedSummaryPanel)}
          </>
        ) : (
          <>
            {hasRecommendedPLan
              && <PreselectedPlanBanner planName={subscription?.plan} />}
            {showSummary && SideViewWrapper(!isLoggedOut, summaryDrawer)}
          </>
        )
      )}
    </>
  );
}

export default Checkout;
